import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, Loader2 } from "lucide-react";
import { Reveal } from "../Reveal";
import { ImageCaptcha } from "../ImageCaptcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Name", type: "text", placeholder: "Your full name" },
  { name: "organization", label: "Organization", type: "text", placeholder: "Company / institution" },
  { name: "email", label: "Email", type: "email", placeholder: "you@organization.com" },
  { name: "project", label: "Project Type / Location", type: "text", placeholder: "e.g. Green township, Pune" },
];

const EMPTY = { name: "", organization: "", email: "", project: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      toast.error("Please complete the security check first.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, captcha_token: captchaToken });
      toast.success("Thank you — the consortium will get back to you shortly.");
      setForm(EMPTY);
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-secondary py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20 lg:px-8">
        <Reveal>
          <div className="mb-4 font-body text-xs font-semibold uppercase tracking-[0.2em] text-forest-600">
            Contact
          </div>
          <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-forest sm:text-4xl">
            Let's explore your project.
          </h2>
          <p className="mt-5 font-body text-base leading-relaxed text-muted-foreground">
            Share your project type, location, and objectives, and the consortium will respond with
            how we can support planning, implementation, and long-term performance.
          </p>
          <div className="mt-10 flex items-center gap-4 rounded-2xl border border-border bg-white p-5">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-forest">
              <Mail className="text-lime-bright" size={20} />
            </div>
            <div>
              <div className="font-body text-xs font-medium uppercase tracking-wider text-muted-foreground">
                General Enquiries
              </div>
              <div className="font-heading text-sm font-bold text-forest">
                connect@frontiersinfra.com
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-2xl border border-border bg-white p-7 lg:p-9"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {FIELDS.map((field) => (
                <div key={field.name} className={field.name === "project" ? "sm:col-span-2" : ""}>
                  <label className="mb-1.5 block font-body text-sm font-medium text-forest">
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    name={field.name}
                    value={form[field.name]}
                    onChange={update}
                    required
                    placeholder={field.placeholder}
                    data-testid={`contact-input-${field.name}`}
                    className="w-full rounded-lg border border-input bg-white px-4 py-3 font-body text-sm text-forest outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-forest"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="mb-1.5 block font-body text-sm font-medium text-forest">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={update}
                  required
                  rows={4}
                  placeholder="Tell us about your objectives..."
                  data-testid="contact-input-message"
                  className="w-full resize-none rounded-lg border border-input bg-white px-4 py-3 font-body text-sm text-forest outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-forest"
                />
              </div>
            </div>
            <div className="mt-6">
              <ImageCaptcha
                key={captchaKey}
                token={captchaToken}
                onVerified={setCaptchaToken}
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !captchaToken}
              data-testid="contact-submit-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-7 py-3.5 font-body text-sm font-semibold text-white transition-colors hover:bg-forest-600 disabled:opacity-60 sm:w-auto"
            >
              {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
              {submitting ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
